/**
 * DailyLogView — Single-day two-column layout.
 * Left: calorie summary + meals. Right: Whoop + exercise.
 * Daily score (1-10) for past days. Cannot navigate past today.
 */
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/features/auth/hooks/useSession';
import { api } from '@/lib/api';

interface DailyLogViewProps {
  healthStats: any[];
  onSyncWhoop?: () => void;
  onReconnectWhoop?: () => void;
  syncing?: boolean;
  connecting?: boolean;
  whoopConnected?: boolean;
  onSilentSendMessage?: (message: string, images?: Array<{ mediaType: string; data: string; filename: string }>) => Promise<void>;
  onOpenLogModal?: () => void;
  onSaveVitals?: (data: { weight?: number; systolic?: number; diastolic?: number }) => Promise<void>;
}

const MEAL_ORDER = ['breakfast', 'lunch', 'dinner', 'snack'] as const;
const MEAL_LABELS: Record<string, string> = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snack: 'Snack' };

function formatDateNav(dateStr: string): string {
  try {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return dateStr; }
}

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function shiftDate(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return toDateStr(new Date(y, m - 1, d + days));
}

/** Generate a life-coach style daily analysis — works for today (forward-looking) and past days (retrospective) */
function generateDayAnalysis(
  consumedCals: number, dailyTarget: number,
  exerciseItems: any[], burnedCals: number,
  whoopRecovery: number | undefined, whoopSleep: number | undefined,
  sleepHours: number | undefined,
  isToday: boolean, mealsLogged: number,
): string | null {
  const lines: string[] = [];
  const remaining = dailyTarget - consumedCals;

  if (isToday) {
    // Forward-looking analysis for today
    if (consumedCals > 0) {
      const ratio = consumedCals / dailyTarget;
      if (ratio >= 0.9 && ratio <= 1.05) {
        lines.push('You\'ve hit your calorie target for the day — nice work. If you snack later, keep it light.');
      } else if (ratio >= 0.7 && ratio < 0.9) {
        lines.push(`${consumedCals.toLocaleString()} cal so far — you've got about ${remaining.toLocaleString()} cal left. Room for a good meal without going over.`);
      } else if (ratio < 0.5 && mealsLogged <= 1) {
        lines.push(`Only ${consumedCals.toLocaleString()} cal so far — you've got ${remaining.toLocaleString()} left. Make sure you eat enough today.`);
      } else if (ratio < 0.7) {
        lines.push(`${consumedCals.toLocaleString()} cal logged with ${remaining.toLocaleString()} remaining. Don't skip dinner — aim for something balanced.`);
      } else if (remaining > 0 && remaining < 300) {
        lines.push(`Almost at target — only ${remaining.toLocaleString()} cal to go. Keep dinner light, or you're done for the day.`);
      } else if (remaining <= 0 && remaining > -200) {
        lines.push(`Slightly over target — no big deal. Just skip the late-night snack.`);
      } else if (remaining <= -200) {
        lines.push(`${Math.abs(remaining).toLocaleString()} cal over target. It happens — don't stress, just be mindful the rest of the evening.`);
      }
    }

    // Exercise nudge for today
    if (exerciseItems.length > 0) {
      const names = exerciseItems.map(i => i.name).filter(Boolean);
      lines.push(`Good work getting ${names.length ? names.join(', ').toLowerCase() : 'exercise'} in today.`);
    } else if (consumedCals > 0) {
      lines.push('No exercise yet today — even 20 minutes of movement will make a difference.');
    }

    // Sleep/recovery from last night
    if (sleepHours != null) {
      if (sleepHours < 6) lines.push(`You only got ${sleepHours.toFixed(1)} hours of sleep last night — take it easy today and get to bed early.`);
      else if (sleepHours < 7) lines.push(`${sleepHours.toFixed(1)} hours of sleep — not bad, but aim for more tonight.`);
    }

    if (whoopRecovery != null) {
      if (whoopRecovery >= 67) lines.push('Recovery is green — great day to push yourself.');
      else if (whoopRecovery < 34) lines.push('Recovery is low — keep today\'s intensity light.');
    }
  } else {
    // Retrospective analysis for past days
    if (consumedCals > 0) {
      const ratio = consumedCals / dailyTarget;
      if (ratio >= 0.9 && ratio <= 1.05) {
        lines.push('Nailed your nutrition — right on target.');
      } else if (ratio >= 0.75 && ratio < 0.9) {
        lines.push(`Ate a bit light at ${consumedCals.toLocaleString()} cal. Try to get closer to your ${dailyTarget.toLocaleString()} target.`);
      } else if (ratio < 0.75) {
        lines.push(`Only ${consumedCals.toLocaleString()} cal — well under target. Make sure you're fueling enough.`);
      } else if (consumedCals - dailyTarget <= 200) {
        lines.push(`Slightly over target at ${consumedCals.toLocaleString()} cal — nothing to worry about.`);
      } else {
        lines.push(`Went ${(consumedCals - dailyTarget).toLocaleString()} cal over target. Fine occasionally, but watch for patterns.`);
      }
    }

    if (exerciseItems.length > 0) {
      const names = exerciseItems.map(i => i.name).filter(Boolean);
      if (burnedCals >= 400) {
        lines.push(`Strong workout${names.length ? ' — ' + names.join(', ').toLowerCase() : ''}. ${burnedCals} calories burned.`);
      } else if (burnedCals >= 150) {
        lines.push(`Good active day${names.length ? ' with ' + names.join(', ').toLowerCase() : ''}.`);
      } else {
        lines.push(`Light activity${names.length ? ' — ' + names.join(', ').toLowerCase() : ''}. Consider pushing harder next time.`);
      }
    } else if (consumedCals > 0) {
      lines.push('No exercise logged. Even a short walk makes a difference.');
    }

    if (sleepHours != null) {
      if (sleepHours >= 7.5) lines.push(`Solid ${sleepHours.toFixed(1)} hours of sleep.`);
      else if (sleepHours >= 6) lines.push(`${sleepHours.toFixed(1)} hours of sleep — okay but aim for 7–8.`);
      else lines.push(`Only ${sleepHours.toFixed(1)} hours of sleep — prioritize rest.`);
    } else if (whoopSleep != null) {
      if (whoopSleep >= 85) lines.push('Sleep quality was excellent.');
      else if (whoopSleep < 50) lines.push('Sleep was rough — get to bed earlier tonight.');
    }

    if (whoopRecovery != null) {
      if (whoopRecovery >= 67) lines.push('Recovery was green — body was ready to go.');
      else if (whoopRecovery < 34) lines.push('Recovery was in the red — rest day was the right call.');
    }
  }

  if (lines.length === 0) return null;
  return lines.join(' ');
}

export default function DailyLogView({ healthStats, onSyncWhoop, onReconnectWhoop, syncing, connecting, whoopConnected, onStatsChanged, onSilentSendMessage, onOpenLogModal, onSaveVitals }: DailyLogViewProps & { onStatsChanged?: () => void }) {
  const todayStr = toDateStr(new Date());
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const isToday = selectedDate === todayStr;

  const goForward = () => {
    const next = shiftDate(selectedDate, 1);
    if (next <= todayStr) setSelectedDate(next);
  };

  useEffect(() => {
    const handler = (e: Event) => {
      const date = (e as CustomEvent).detail?.date;
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) setSelectedDate(date);
    };
    window.addEventListener('health-navigate-date', handler);
    return () => window.removeEventListener('health-navigate-date', handler);
  }, []);

  // Fetch profile separately (it's excluded from healthStats)
  const { token } = useSession();
  const { data: profile } = useQuery({
    queryKey: ['health-profile-daily'],
    queryFn: async () => {
      const result = await api.objects.getObjectsByType(token ?? null, 'stat', { limit: 200 });
      const items = result?.items || [];
      const profileStats = items.filter((s: any) => (s.source || s.attributes?.source) === 'health:profile');
      if (profileStats.length === 0) return null;
      // Merge all profile stats (same logic as HealthProfile.tsx)
      const merged: Record<string, unknown> = {};
      for (const stat of profileStats) {
        for (const [key, val] of Object.entries(stat)) {
          if (val != null && val !== '') merged[key] = val;
        }
      }
      return merged;
    },
    enabled: Boolean(token),
    staleTime: 30_000,
  });
  const dailyTarget = (profile?.dailyCalorieTarget || profile?.calorieTarget || 1800) as number;

  const dayStats = useMemo(() => healthStats.filter(s => (s.date || s.attributes?.date || '').slice(0, 10) === selectedDate), [healthStats, selectedDate]);
  const meals = useMemo(() => dayStats.filter(s => (s.source || s.attributes?.source || '') === 'health:food'), [dayStats]);
  const exercise = useMemo(() => dayStats.filter(s => (s.source || s.attributes?.source || '') === 'health:exercise'), [dayStats]);
  const whoopData = useMemo(() => dayStats.filter(s => (s.source || s.attributes?.source || '').startsWith('whoop:')), [dayStats]);

  const mealsByType = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    for (const m of meals) {
      const mt = (m.meal || m.attributes?.meal || 'snack').toLowerCase();
      if (!grouped[mt]) grouped[mt] = [];
      grouped[mt].push(m);
    }
    return grouped;
  }, [meals]);

  const consumedCals = useMemo(() => meals.reduce((s: number, m: any) => s + (m.totalCalories || m.attributes?.totalCalories || 0), 0), [meals]);
  const burnedCals = useMemo(() => exercise.reduce((s: number, e: any) => s + (e.totalCaloriesBurned || e.attributes?.totalCaloriesBurned || e.calories || e.attributes?.calories || 0), 0), [exercise]);
  const overTarget = consumedCals - dailyTarget;

  // Weight & blood pressure from profile (fetched separately above)
  const weightValue = useMemo(() => {
    // Check daily vitals stats — find weight entry
    const weightStat = dayStats.find((s: any) => {
      const src = s.source || s.attributes?.source || '';
      const label = (s.label || s.attributes?.label || '').toLowerCase();
      return src === 'health:vitals' && label.includes('weight');
    });
    if (weightStat) return weightStat.value ?? weightStat.attributes?.value ?? weightStat.weight ?? null;
    if (!profile) return null;
    return profile.weight || profile.weightDisplay || null;
  }, [dayStats, profile]);

  const weightUnit = (profile?.weightUnit || 'lbs') as string;

  const bloodPressure = useMemo(() => {
    // Check daily vitals stats — find most recent BP entry (sort descending by createdAt so newest wins)
    const bpStat = [...dayStats].sort((a: any, b: any) => (b.createdAt || '').localeCompare(a.createdAt || '')).find((s: any) => {
      const src = s.source || s.attributes?.source || '';
      const label = (s.label || s.attributes?.label || '').toLowerCase();
      if (src !== 'health:vitals') return false;
      // Match by label
      if (label.includes('blood') || label.includes('pressure')) return true;
      // Match by bloodPressure field presence (agent stores BP this way with label "Vitals")
      const bp = s.bloodPressure || s.attributes?.bloodPressure;
      if (bp && String(bp).includes('/')) return true;
      // Match by value containing "/" (e.g. "120/80")
      const val = String(s.value || s.attributes?.value || '');
      if (val.match(/^\d+\/\d+$/)) return true;
      return false;
    });
    if (bpStat) {
      const sys = bpStat.systolic || bpStat.attributes?.systolic;
      const dia = bpStat.diastolic || bpStat.attributes?.diastolic;
      if (sys && dia) return { systolic: Number(sys), diastolic: Number(dia) };
      // Check bloodPressure string field
      const bp = String(bpStat.bloodPressure || bpStat.attributes?.bloodPressure || '');
      if (bp.includes('/')) {
        const [s, d] = bp.split('/').map(Number);
        if (s && d) return { systolic: s, diastolic: d };
      }
      const val = String(bpStat.value || bpStat.attributes?.value || '');
      if (val.includes('/')) {
        const [s, d] = val.split('/').map(Number);
        if (s && d) return { systolic: s, diastolic: d };
      }
    }
    // Fallback to profile
    if (!profile) return null;
    const sys = profile.systolic || profile.bloodPressureSystolic;
    const dia = profile.diastolic || profile.bloodPressureDiastolic;
    if (sys && dia) return { systolic: sys, diastolic: dia };
    const bpStr = profile.bloodPressure as string | undefined;
    if (bpStr && String(bpStr).includes('/')) {
      const [s, d] = String(bpStr).split('/').map(Number);
      if (s && d) return { systolic: s, diastolic: d };
    }
    return null;
  }, [dayStats, profile]);
  const mealsWithData = MEAL_ORDER.filter(mt => (mealsByType[mt] || []).length > 0);
  const allExerciseItems = useMemo(() => {
    const items = exercise.flatMap((ex: any) => ex.items || ex.attributes?.items || []);
    // Deduplicate by name+duration (upsert can create duplicate stat entries)
    const seen = new Set<string>();
    return items.filter((item: any) => {
      const key = `${item.name}|${item.duration || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [exercise]);

  // Whoop metrics
  const getWhoopMetric = (key: string) => whoopData.find((w: any) => (w.source || w.attributes?.source || '') === `whoop:${key}`);
  const getVal = (w: any) => w?.value ?? w?.attributes?.value;
  const recoveryData = getWhoopMetric('recovery');
  const sleepData = getWhoopMetric('sleep');
  const strainData = getWhoopMetric('strain');
  const recoveryVal = getVal(recoveryData);
  const sleepVal = getVal(sleepData);
  const strainVal = getVal(strainData);
  const whoopCalories = strainData?.calories || strainData?.attributes?.calories || strainData?.metadata?.calories;

  // Sleep hours for analysis
  const sleepHours = sleepData?.durationHours || sleepData?.attributes?.durationHours || undefined;

  // Daily analysis — always show coaching
  const dayAnalysis = useMemo(() => {
    // Always generate — even with no data, give a nudge
    const analysis = generateDayAnalysis(consumedCals, dailyTarget, allExerciseItems, burnedCals, recoveryVal, sleepVal, sleepHours, isToday, mealsWithData.length);
    if (analysis) return analysis;
    // Fallback coaching when there's truly nothing
    if (isToday) return 'No data logged yet. Start by logging your meals — just tell me what you ate.';
    return 'Nothing logged for this day. You can still add meals retroactively — just say what you had.';
  }, [consumedCals, dailyTarget, allExerciseItems, burnedCals, recoveryVal, sleepVal, sleepHours, isToday, mealsWithData.length]);

  const [removingItem, setRemovingItem] = useState<string | null>(null);
  const [editingVital, setEditingVital] = useState<'weight' | 'bp' | null>(null);
  const [vitalInput, setVitalInput] = useState('');
  const removeFoodItem = async (statId: string, items: any[], itemIdx: number, etag?: string) => {
    if (!token || removingItem) return;
    const itemKey = `${statId}-${itemIdx}`;
    setRemovingItem(itemKey);
    const remaining = items.filter((_: any, i: number) => i !== itemIdx);
    try {
      if (remaining.length === 0) {
        await api.objects.deleteObject(token, 'stat', statId);
      } else {
        const totalCalories = remaining.reduce((s: number, i: any) => s + (i.calories || 0), 0);
        const totalProtein = remaining.reduce((s: number, i: any) => s + (i.protein || 0), 0);
        await api.objects.updateObject(token, 'stat', statId, { items: remaining, totalCalories, totalProtein, value: totalCalories }, etag);
      }
      onStatsChanged?.();
    } catch (err) {
      console.error('[DailyLogView] Failed to remove food item:', err);
    } finally {
      setRemovingItem(null);
    }
  };


  const navBtnStyle: React.CSSProperties = {
    background: 'none', border: '1px solid var(--color-border-primary)',
    borderRadius: 8, width: 44, height: 44, padding: 0, cursor: 'pointer',
    color: 'var(--color-text-secondary)', fontSize: 18, fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif", lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Date navigation + Sync */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0 10px' }}>
        <button onClick={() => setSelectedDate(shiftDate(selectedDate, -1))} style={{ ...navBtnStyle, width: 32, height: 32 }}>&#8592;</button>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)' }}>
          {formatDateNav(selectedDate)}{isToday ? ' · Today' : ''}
        </span>
        <button onClick={goForward} disabled={isToday} style={{ ...navBtnStyle, width: 32, height: 32, opacity: isToday ? 0.5 : 1, cursor: isToday ? 'default' : 'pointer' }}>&#8594;</button>
      </div>

      {/* Calorie ring + stats card */}
      <div style={{
        padding: '16px', borderRadius: 12, marginBottom: 12,
        background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        display: 'flex', alignItems: 'center', gap: 20,
      }}>
        {/* SVG Ring */}
        <div style={{ position: 'relative', width: 100, height: 100, flexShrink: 0 }}>
          <svg width="100" height="100" viewBox="0 0 100 100">
            {/* Track */}
            <circle cx="50" cy="50" r="42" fill="none" stroke="#f1f5f9" strokeWidth="8"
              strokeDasharray={consumedCals > 0 ? undefined : '4 4'} />
            {/* Progress */}
            {consumedCals > 0 && (
              <circle cx="50" cy="50" r="42" fill="none"
                stroke={overTarget > 0 ? '#ef4444' : '#0ea5e9'} strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${Math.min((consumedCals / dailyTarget) * 264, 264)} 264`}
                transform="rotate(-90 50 50)"
                style={{ transition: 'stroke-dasharray 0.5s ease' }}
              />
            )}
          </svg>
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
              {consumedCals > 0 ? consumedCals.toLocaleString() : '0'}
            </span>
            <span style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 2 }}>/ {dailyTarget.toLocaleString()}</span>
          </div>
        </div>
        {/* Stats column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.04em' }}>Calories</span>
            <span style={{ color: 'var(--color-text-faint)', fontVariantNumeric: 'tabular-nums' }}>{consumedCals > 0 ? consumedCals.toLocaleString() : '0'} / {dailyTarget.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.04em' }}>Protein</span>
            <span style={{ color: 'var(--color-text-faint)', fontVariantNumeric: 'tabular-nums' }}>
              {(() => {
                const totalProtein = meals.reduce((sum: number, m: any) => {
                  // Check totalProtein on stat first, fall back to summing items
                  const mealProtein = m.totalProtein || m.attributes?.totalProtein;
                  if (mealProtein) return sum + mealProtein;
                  const items = m.items || m.attributes?.items || [];
                  return sum + items.reduce((s: number, i: any) => s + (i.protein || 0), 0);
                }, 0);
                const proteinTarget = profile?.proteinTarget || 120;
                return totalProtein > 0 ? `${Math.round(totalProtein)}g / ${proteinTarget}g` : `0 / ${proteinTarget}g`;
              })()}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.04em' }}>Exercise</span>
            <span style={{ color: 'var(--color-text-faint)' }}>{allExerciseItems.length > 0 ? allExerciseItems.map((i: any) => i.name).join(', ') : 'No activity'}</span>
          </div>
          {burnedCals > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.04em' }}>Burned</span>
              <span style={{ color: 'var(--color-text-faint)', fontVariantNumeric: 'tabular-nums' }}>{burnedCals.toLocaleString()} cal</span>
            </div>
          )}
        </div>
      </div>

      {/* Daily analysis */}
      {dayAnalysis && (
        <div style={{
          padding: '14px 16px', borderRadius: 12, marginBottom: 12,
          background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Daily Analysis</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-faint)', lineHeight: 1.6 }}>
            {dayAnalysis}
          </div>
        </div>
      )}


      {/* Whoop vitals row */}
      {whoopData.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          {sleepData && (
            <div style={{ flex: 1, padding: '14px 16px', borderRadius: 12, background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Sleep</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' }}>
                {sleepHours ? sleepHours.toFixed(1) : sleepVal ?? '—'}
                <span style={{ fontSize: 12, color: 'var(--color-text-faint)' }}>{sleepHours ? 'h' : '%'}</span>
              </div>
            </div>
          )}
          {recoveryData && (
            <div style={{ flex: 1, padding: '14px 16px', borderRadius: 12, background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Recovery</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' }}>
                {recoveryVal ?? '—'}<span style={{ fontSize: 12, color: 'var(--color-text-faint)' }}>%</span>
              </div>
            </div>
          )}
          {strainData && (
            <div style={{ flex: 1, padding: '14px 16px', borderRadius: 12, background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Strain</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' }}>
                {strainVal != null ? (typeof strainVal === 'number' && strainVal % 1 !== 0 ? strainVal.toFixed(1) : strainVal) : '—'}
              </div>
            </div>
          )}
          {whoopCalories != null && whoopCalories > 0 && (
            <div style={{ flex: 1, padding: '14px 16px', borderRadius: 12, background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Burned</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#22c55e', fontVariantNumeric: 'tabular-nums' }}>
                {whoopCalories.toLocaleString()}
                <span style={{ fontSize: 12, color: 'var(--color-text-faint)' }}> cal</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Exercise */}
      {allExerciseItems.length > 0 && (
        <div style={{
          padding: '14px 16px', borderRadius: 12, marginBottom: 8,
          background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)' }}>Exercise</span>
            {burnedCals > 0 && (
              <span style={{ fontSize: 13, fontWeight: 600, color: '#0ea5e9', fontVariantNumeric: 'tabular-nums' }}>{burnedCals.toLocaleString()} cal burned</span>
            )}
          </div>
          {exercise.map((ex: any) => {
            const items = ex.items || ex.attributes?.items || [];
            return items.map((item: any, idx: number) => {
              const itemKey = `${ex.id}-ex-${idx}`;
              return (
                <div key={itemKey} style={{ display: 'flex', alignItems: 'center', fontSize: 14, color: 'var(--color-text-muted)', padding: '3px 0', borderBottom: '1px solid #f1f5f9', gap: 6 }}>
                  <span style={{ flex: 1 }}>{item.name}</span>
                  {item.duration && <span style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>{item.duration}</span>}
                  {item.caloriesBurned != null && <span style={{ fontSize: 13, color: 'var(--color-text-faint)', fontVariantNumeric: 'tabular-nums' }}>{item.caloriesBurned} cal</span>}
                  <button
                    onClick={() => removeFoodItem(ex.id, items, idx, ex._etag || ex.etag)}
                    disabled={removingItem !== null}
                    style={{
                      background: 'none', border: 'none', cursor: removingItem ? 'default' : 'pointer', padding: '0 2px',
                      color: removingItem === itemKey ? '#9ca3af' : '#d1d5db',
                      fontSize: 14, lineHeight: 1, flexShrink: 0,
                      opacity: removingItem !== null && removingItem !== itemKey ? 0.3 : 1,
                    }}
                    onMouseEnter={e => { if (!removingItem) e.currentTarget.style.color = '#ef4444'; }}
                    onMouseLeave={e => { if (!removingItem) e.currentTarget.style.color = '#d1d5db'; }}
                    title="Remove item"
                  >&#x2715;</button>
                </div>
              );
            });
          })}
        </div>
      )}

      {/* Divider between exercise and meals */}
      {allExerciseItems.length > 0 && mealsWithData.length > 0 && (
        <div style={{ height: 1, background: '#e5e7eb', margin: '4px 0 8px' }} />
      )}

      {/* Meals */}
      {mealsWithData.length === 0 ? (
        /* Empty state — consolidated card */
        <div style={{
          padding: '32px 20px', borderRadius: 12, textAlign: 'center',
          background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🍽️</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)', marginBottom: 4 }}>No meals logged yet</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-faint)', lineHeight: 1.5 }}>
            Tap a quick log above or just tell<br />Xavior what you ate below
          </div>
        </div>
      ) : (
        /* Meal cards — only show types with data */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {mealsWithData.map(mealType => {
            const entries = mealsByType[mealType] || [];
            const mealCals = entries.reduce((s: number, m: any) => s + (m.totalCalories || m.attributes?.totalCalories || 0), 0);
            return (
              <div key={mealType} style={{
                padding: '14px 16px', borderRadius: 12,
                background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)' }}>{MEAL_LABELS[mealType]}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#0ea5e9', fontVariantNumeric: 'tabular-nums' }}>{mealCals} cal</span>
                </div>
                {entries.map((meal: any) => {
                  const items = meal.items || meal.attributes?.items || [];
                  return (
                    <div key={meal.id}>
                      {items.map((item: any, idx: number) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', fontSize: 14, color: 'var(--color-text-muted)', padding: '3px 0', borderBottom: '1px solid #f1f5f9', gap: 6 }}>
                          <span style={{ flex: 1 }}>{item.name}</span>
                          {item.calories != null && <span style={{ fontSize: 13, color: 'var(--color-text-faint)', fontVariantNumeric: 'tabular-nums' }}>{item.calories}</span>}
                          <button
                            onClick={() => removeFoodItem(meal.id, items, idx, meal._etag || meal.etag)}
                            disabled={removingItem !== null}
                            style={{
                              background: 'none', border: 'none', cursor: removingItem ? 'default' : 'pointer', padding: '0 2px',
                              color: removingItem === `${meal.id}-${idx}` ? '#9ca3af' : '#d1d5db',
                              fontSize: 14, lineHeight: 1, flexShrink: 0,
                              opacity: removingItem !== null && removingItem !== `${meal.id}-${idx}` ? 0.3 : 1,
                            }}
                            onMouseEnter={e => { if (!removingItem) e.currentTarget.style.color = '#ef4444'; }}
                            onMouseLeave={e => { if (!removingItem) e.currentTarget.style.color = '#d1d5db'; }}
                            title="Remove item"
                          >
                            &#x2715;
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Weight & Blood Pressure row — inline editable */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <div
          onClick={() => !editingVital && setEditingVital('weight')}
          style={{
            flex: 1, padding: '14px 16px', borderRadius: 12, cursor: editingVital === 'weight' ? 'default' : 'pointer',
            background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Weight</div>
          {editingVital === 'weight' ? (
            <form onSubmit={(e) => { e.preventDefault(); const v = parseFloat(vitalInput); if (v > 0 && onSaveVitals) { onSaveVitals({ weight: v }); } setEditingVital(null); setVitalInput(''); }} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input autoFocus type="number" step="0.1" value={vitalInput} onChange={e => setVitalInput(e.target.value)} placeholder="lbs" onBlur={() => { setEditingVital(null); setVitalInput(''); }}
                style={{ width: 70, height: 32, padding: '0 8px', border: '1px solid #0ea5e9', borderRadius: 6, fontSize: 16, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", outline: 'none', color: 'var(--color-text)' }} />
              <span style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>lbs</span>
            </form>
          ) : (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              {weightValue ? (
                <>
                  <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' }}>{weightValue}</span>
                  <span style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>{weightUnit}</span>
                </>
              ) : (
                <span style={{ fontSize: 14, color: '#d1d5db' }}>Tap to log</span>
              )}
            </div>
          )}
        </div>
        <div
          onClick={() => !editingVital && setEditingVital('bp')}
          style={{
            flex: 1, padding: '14px 16px', borderRadius: 12, cursor: editingVital === 'bp' ? 'default' : 'pointer',
            background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Blood Pressure</div>
          {editingVital === 'bp' ? (
            <form onSubmit={(e) => { e.preventDefault(); const parts = vitalInput.split('/').map(Number); if (parts.length === 2 && parts[0] > 0 && parts[1] > 0 && onSaveVitals) { onSaveVitals({ systolic: parts[0], diastolic: parts[1] }); } setEditingVital(null); setVitalInput(''); }} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input autoFocus type="text" value={vitalInput} onChange={e => setVitalInput(e.target.value)} placeholder="120/80" onBlur={() => { setEditingVital(null); setVitalInput(''); }}
                style={{ width: 80, height: 32, padding: '0 8px', border: '1px solid #0ea5e9', borderRadius: 6, fontSize: 16, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", outline: 'none', color: 'var(--color-text)' }} />
              <span style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>mmHg</span>
            </form>
          ) : (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              {bloodPressure ? (
                <>
                  <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' }}>{bloodPressure.systolic}/{bloodPressure.diastolic}</span>
                  <span style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>mmHg</span>
                </>
              ) : (
                <span style={{ fontSize: 14, color: '#d1d5db' }}>Tap to log</span>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
